import {
  createColumn,
  createCheckboxColumn,
  createDropdownColumn
} from "../components/Table/Table";

import { decodeValueDataFormat, getKoneFormatString } from "../utils/pdApiUtils";
import { base64ToHex, convertFormattedValueString } from "../utils/utils";

// Date and User columns common
const parametersDateUserColumn: State.Column[] = [
  createColumn(
    "Created By",
    (parameter: Api.AnyItem) => parameter.createdBy,
    "parameter.createdBy",
    false
  ),
  createColumn(
    "Created Date",
    (parameter: Api.AnyItem) => parameter.createdDate,
    "parameter.createdDate",
    false
  ),
  createColumn(
    "Modified By",
    (parameter: Api.AnyItem) => parameter.modifiedBy,
    "parameter.modifiedBy",
    false
  ),
  createColumn(
    "Modified Date",
    (parameter: Api.AnyItem) => parameter.modifiedDate,
    "parameter.modifiedDate",
    false
  )
];

// Date and User columns common
const liftParametersDateUserColumn: State.Column[] = [
  createColumn(
    "Created By",
    (parameter: Api.AnyItem) => parameter.createdBy,
    "parameter.createdBy",
    false
  ),
  createColumn(
    "Created Date",
    (parameter: Api.AnyItem) => parameter.created,
    "parameter.created",
    false
  ),
  createColumn(
    "Modified By",
    (parameter: Api.AnyItem) => parameter.modifiedBy,
    "parameter.modifiedBy",
    false
  ),
  createColumn(
    "Modified Date",
    (parameter: Api.AnyItem) => parameter.modifiedDate,
    "parameter.modifiedDate",
    false
  )
];

// Columns in Drive Parameter submenu
export const driveParametersColumns: State.Column[] = [
  createDropdownColumn(
    "Domain",
    (parameter: Api.AnyItem) => parameter.commonExtra.domainName,
    "parameter.commonExtra.domainName",
    true,
    110
  ),
  createColumn(
    "Code",
    (parameter: Api.BaseParameter) => parameter.code,
    "parameter.code",
    true,
    80
  ),
  createColumn(
    "Sw Name",
    (parameter: Api.DriveParameter) => parameter.enumName,
    "parameter.enumName",
    true
  ),
  createDropdownColumn(
    "Category",
    (parameter: Api.AnyItem) => parameter.commonExtra.categoryName,
    "parameter.commonExtra.categoryName",
    true
  ),
  createColumn(
    "Stored Index",
    (parameter: Api.DriveParameter) => parameter.storeIndex,
    "parameter.storeIndex",
    true
  ),
  createColumn(
    "Data Format",
    (parameter: Api.DriveParameter) =>
      getKoneFormatString(decodeValueDataFormat(parameter.valueDataFormat)),
    "parameter.valueDataFormat",
    true
  ),
  createColumn(
    "Default",
    (parameter: Api.DriveParameter) => {
      const decodedValue = decodeValueDataFormat(parameter.valueDataFormat);
      const decimals =
        decodedValue.range === "Number" && decodedValue.decimals ? decodedValue.decimals : 0;
      let swDefaultValue = parameter.swDefaultValue;
      if (decimals > 0) {
        swDefaultValue *= 1 / Math.pow(10, decimals);
      }
      return Number(swDefaultValue.toFixed(decimals));
    },
    "parameter.valueDataFormat",
    true
  ),
  createColumn(
    "Unit",
    (parameter: Api.DriveParameter) => parameter.unitName,
    "parameter.unitName",
    true
  ),
  createColumn(
    "Trigger function",
    (parameter: Api.DriveParameter) => parameter.triggerFunction,
    "parameter.triggerFunction",
    true
  ),
  createColumn(
    "Extra scaling",
    (parameter: Api.DriveParameter) => parameter.extraScaling,
    "parameter.extraScaling",
    true
  ),
    createDropdownColumn(
    "Visibility",
    (parameter: Api.DriveParameter) => parameter.visibilityText,
    "parameter.visibilityText",
    true,
    200
  ),
  createCheckboxColumn(
    "Shaft setup",
    (parameter: Api.DriveParameter) => parameter.shaftSetupRequired,
    "parameter.shaftSetupRequired",
    true
  ),
  createCheckboxColumn(
    "Custom motor type",
    (parameter: Api.DriveParameter) => parameter.customMotorType,
    "parameter.customMotorType",
    true
  ),
  createCheckboxColumn(
    "Change motor type",
    (parameter: Api.DriveParameter) => parameter.changeMotorType,
    "parameter.changeMotorType",
    true
  ),
  createCheckboxColumn(
    "Ride comfort",
    (parameter: Api.DriveParameter) => parameter.rideComfort,
    "parameter.rideComfort",
    true
  ),
  createColumn(
    "English name",
    (parameter: Api.DriveParameter) => parameter.name,
    "parameter.name",
    false
  ),
  createColumn(
    "Description",
    (parameter: Api.DriveParameter) => parameter.description,
    "parameter.description",
    false
  ),
  createColumn(
    "Values Type",
    (parameter: Api.DriveParameter) => parameter.preferredValueTypeEnumValue,
    "parameter.preferredValueTypeEnumValue",
    false
  ),
  createColumn(
    "Preferred Values",
    (parameter: Api.DriveParameter) => {
      const decodedValue = decodeValueDataFormat(parameter.valueDataFormat);
      const decimals =
        decodedValue.range === "Number" && decodedValue.decimals ? decodedValue.decimals : 0;
      return parameter.preferredValues
        .map((unformatted: number) => convertFormattedValueString(unformatted, decimals))
        .join(";");
    },

    "parameter.preferredValues",
    false
  ),
  createCheckboxColumn(
    "Localizable",
    (parameter: Api.DriveParameter) => parameter.localizable,
    "parameter.localizable",
    true
  ),
  ...parametersDateUserColumn
];

// Columns in Lift Parameter submenu
export const liftParametersColumns: State.Column[] = [
  createDropdownColumn(
    "Domain",
    (parameter: Api.LiftParameter) => parameter.commonExtra?.domainName,
    "parameter.commonExtra.domainName",
    true,
    110
  ),
  createColumn(
    "Code",
    (parameter: Api.BaseParameter) => parameter.code,
    "parameter.code",
    true,
    80
  ),
  createColumn(
    "Sw Name",
    (parameter: Api.LiftParameter) => parameter.enumName,
    "parameter.enumName",
    true
  ),
  createColumn(
    "Category / SubCategory",
    (parameter: Api.AnyItem) => parameter.commonExtra.categoryPath,
    "parameter.commonExtra.categoryPath",
    true,
    200
  ),
  createColumn(
    "Comment",
    (parameter: Api.LiftParameter) => parameter.comment,
    "parameter.comment",
    true
  ),
  createCheckboxColumn(
    "Requires reset",
    (parameter: Api.LiftParameter) => parameter.requiresReset,
    "parameter.requiresReset",
    true
  ),
  createCheckboxColumn(
    "Requires setup",
    (parameter: Api.LiftParameter) => parameter.requiresSetup,
    "parameter.requiresSetup",
    true
  ),
  createColumn(
    "English name",
    (parameter: Api.LiftParameter) => parameter.name,
    "parameter.name",
    true
  ),
  createColumn(
    "Description",
    (parameter: Api.LiftParameter) => parameter.description,
    "parameter.description",
    false
  ),
  createColumn(
    "Item Count",
    (parameter: Api.LiftParameter) => parameter.itemCount,
    "parameter.ItemCount",
    false
  ),
  createColumn(
    "Max Value",
    (parameter: Api.LiftParameter) => parameter.maxValue,
    "parameter.maxValue",
    false
  ),
  createColumn(
    "Min Value",
    (parameter: Api.LiftParameter) => parameter.minValue,
    "parameter.minValue",
    false
  ),
  createColumn(
    "swDefaultValue",
    (parameter: Api.AnyItem) => parameter.swDefaultValue,
    "parameter.swDefaultValue",
    false
  ),
  ...liftParametersDateUserColumn
];

export const apmParametersColumns: State.Column[] = [
  createDropdownColumn(
    "Domain",
    (parameter: Api.AnyItem) => parameter.commonExtra.domainName,
    "parameter.commonExtra.domainName",
    true,
    140
  ),
  createDropdownColumn(
    "Feature",
    (parameter: Api.GroupParameter) => parameter.feature,
    "parameter.feature",
    true,
    200
  ),
  createColumn(
    "Code",
    (parameter: Api.BaseParameter) => parameter.code,
    "parameter.code",
    true,
    80
  ),
  createColumn(
    "Sw Name",
    (parameter: Api.GroupParameter) => parameter.enumName,
    "parameter.enumName",
    false
  ),
  createColumn(
    "Category",
    (parameter: Api.AnyItem) => parameter.commonExtra.categoryPath,
    "parameter.commonExtra.categoryPath",
    true,
    120
  ),
  createColumn(
    "English name",
    (parameter: Api.BaseParameter) => parameter.name,
    "parameter.name",
    true,
    240
  ),
  createColumn(
    "Description",
    (parameter: Api.BaseParameter) => parameter.description,
    "parameter.description",
    true,
    380
  ),
  createColumn(
    "Release status",
    (parameter: Api.GroupParameter) => parameter.releaseStatus,
    "parameter.releaseStatus",
    true,
    120
  ),
  ...parametersDateUserColumn
];

// Columns in Group Parameter submenu
export const groupParametersColumns: State.Column[] = [
  createDropdownColumn(
    "Domain",
    (parameter: Api.AnyItem) => parameter.commonExtra.domainName,
    "parameter.commonExtra.domainName",
    true,
    140
  ),
  createDropdownColumn(
    "Feature",
    (parameter: Api.GroupParameter) => parameter.feature,
    "parameter.feature",
    true,
    200
  ),
  createColumn(
    "Code",
    (parameter: Api.BaseParameter) => parameter.code,
    "parameter.code",
    true,
    80
  ),
  createColumn(
    "Sw Name",
    (parameter: Api.GroupParameter) => parameter.enumName,
    "parameter.enumName",
    false
  ),
  createColumn(
    "Category",
    (parameter: Api.AnyItem) => parameter.commonExtra.categoryPath,
    "parameter.commonExtra.categoryPath",
    true,
    120
  ),
  createColumn(
    "English name",
    (parameter: Api.BaseParameter) => parameter.name,
    "parameter.name",
    true,
    240
  ),
  createColumn(
    "Description",
    (parameter: Api.BaseParameter) => parameter.description,
    "parameter.description",
    true,
    380
  ),
  createColumn(
    "Release status",
    (parameter: Api.GroupParameter) => parameter.releaseStatus,
    "parameter.releaseStatus",
    true,
    120
  ),
  ...parametersDateUserColumn
];



// Return columns with updated feature options
export const getDependentGroupParametersColumns = (
  groupParametersColumns: State.Column[],
  featureOptions?: string[]
): State.Column[] =>
  groupParametersColumns.map(column => {
    if (column.name === "Feature") {
      return createDropdownColumn(
        "Feature",
        (parameter: Api.GroupParameter) => parameter.feature,
        "parameter.feature",
        column.isVisible,
        200,
        undefined,
        undefined,
        featureOptions
      );
    } else {
      return column;
    }
  });

  export const getDependentDriveParametersColumns = (
  driveParametersColumns: State.Column[],
  featureOptions?: string[]
): State.Column[] =>
  driveParametersColumns.map(column => {
    if (column.name === "Visibility") {
      return createDropdownColumn(
        "Visibility",
        (parameter: Api.DriveParameter) => parameter.visibilityText,
        "parameter.visibilityText",
        column.isVisible,
        200,
        undefined,
        undefined,
        featureOptions
      );
    } else {
      return column;
    }
  });

// Columns in Safety Parameter submenu
export const safetyParametersColumns: State.Column[] = [
  createColumn(
    "Code",
    (parameter: Api.BaseParameter) => parameter.code,
    "parameter.code",
    true,
    80
  ),
  createColumn(
    "Sw Name",
    (parameter: Api.BaseParameter) => parameter.enumName,
    "parameter.enumName",
    true
  ),
    createDropdownColumn(
    "Domain",
    (parameter: Api.BaseParameter) => parameter.commonExtra?.domainName,
    "parameter.commonExtra.domainName",
    true,
    180
  ),
  createDropdownColumn(
    "Category",
    (parameter: Api.BaseParameter) => parameter.commonExtra?.categoryPath,
    "parameter.commonExtra.categoryName",
    true,
    200
  ),
  createColumn(
    "English name",
    (parameter: Api.BaseParameter) => parameter.name,
    "parameter.name",
    true
  ),
  createColumn(
    "Description",
    (parameter: Api.BaseParameter) => parameter.description,
    "parameter.description",
    true
  ),
  createColumn(
    "Max Value",
    (parameter: Api.SafetyParameter) => parameter.maxValue,
    "parameter.maxValue",
    false
  ),
  createColumn(
    "Min Value",
    (parameter: Api.SafetyParameter) => parameter.minValue,
    "parameter.minValue",
    false
  ),
  createColumn(
    "Byte Count",
    (parameter: Api.SafetyParameter) => parameter.byteCount,
    "parameter.byteCount",
    false
  ),
  createColumn(
    "Byte Index",
    (parameter: Api.SafetyParameter) => parameter.byteIndex,
    "parameter.byteIndex",
    false
  ),
  createColumn(
    "Factor",
    (parameter: Api.SafetyParameter) => parameter.factor,
    "parameter.factor",
    false
  ),
  createColumn(
    "Bit Mask",
    (parameter: Api.SafetyParameter) => base64ToHex(String(parameter.bitMask ?? "")),
    "parameter.bitMask",
    false
  ),
  createColumn(
    "Unit name",
    (parameter: Api.SafetyParameter) => parameter.unit,
    "parameter.unitName",
    false
  ),
  createDropdownColumn(
    "Data Type",
    (parameter: Api.SafetyParameter) => parameter.dataTypeName ?? String(parameter.dataType),
    "parameter.dataTypeName",
    false,
    180
  ),
    createCheckboxColumn(
    "Read Only",
    (parameter: Api.SafetyParameter) => parameter.readOnly,
    "parameter.readOnly",
    false
  ),
    createCheckboxColumn(
    "Sub Title Row ",
    (parameter: Api.SafetyParameter) => parameter.isSubTitleRow,
    "parameter.isSubTitleRow",
    false
  ),
  ...parametersDateUserColumn
];

// Columns in Escalator Parameter submenu
export const escalatorParametersColumns: State.Column[] = [
  createDropdownColumn(
    "Domain",
    (parameter: Api.BaseParameter) => parameter.commonExtra?.domainName,
    "parameter.commonExtra.domainName",
    true,
    180
  ),
  createColumn(
    "Code",
    (parameter: Api.BaseParameter) => parameter.code,
    "parameter.code",
    true,
    80
  ),
  createColumn(
    "Sw Name",
    (parameter: Api.BaseParameter) => parameter.enumName,
    "parameter.enumName",
    true,
    300
  ),
  createDropdownColumn(
    "Category",
    (parameter: Api.BaseParameter) => parameter.commonExtra?.categoryName,
    "parameter.commonExtra.categoryName",
    true,
    200
  ),
  createColumn(
    "Data Format",
    (parameter: Api.EscalatorParameter) =>
      getKoneFormatString(decodeValueDataFormat(parameter.valueDataFormat)),
    "parameter.valueDataFormat",
    true,
    100
  ),
  createColumn(
    "Default",
    (parameter: Api.EscalatorParameter) => {
      const decodedValue = decodeValueDataFormat(parameter.valueDataFormat);
      const decimals =
        decodedValue.range === "Number" && decodedValue.decimals ? decodedValue.decimals : 0;
      let swDefaultValue = parameter.swDefaultValue;
      if (decimals > 0) {
        swDefaultValue *= 1 / Math.pow(10, decimals);
      }
      return swDefaultValue.toFixed(decimals);
    },
    "parameter.valueDataFormat",
    true,
    60
  ),
  createColumn(
    "Unit",
    (parameter: Api.EscalatorParameter) => parameter.unitName,
    "parameter.unitName",
    true,
    60
  ),
  createColumn(
    "Comment",
    (parameter: Api.EscalatorParameter) => parameter.comment,
    "parameter.comment",
    true
  ),
  createDropdownColumn(
    "Param type",
    (parameter: Api.EscalatorParameter) => parameter.escalatorParamTypeEnum,
    "parameter.escalatorParamTypeEnum",
    true,
    150
  ),
  createDropdownColumn(
    "Param format",
    (parameter: Api.EscalatorParameter) => parameter.escalatorParamFormatEnum,
    "parameter.escalatorParamFormatEnum",
    true,
    150
  ),
  createCheckboxColumn(
    "Requires reset",
    (parameter: Api.EscalatorParameter) => parameter.requiresReset,
    "parameter.requiresReset",
    true,
    100
  ),
  createCheckboxColumn(
    "Requires setup",
    (parameter: Api.EscalatorParameter) => parameter.requiresSetup,
    "parameter.requiresSetup",
    true,
    100
  ),
  createCheckboxColumn(
    "Requires inspection",
    (parameter: Api.EscalatorParameter) => parameter.paramInspection,
    "parameter.paramInspection",
    true,
    100
  ),
  createCheckboxColumn(
    "Modification in running",
    (parameter: Api.EscalatorParameter) => parameter.accessInRunning,
    "parameter.accessInRunning",
    true,
    100
  ),
  createColumn(
    "English name",
    (parameter: Api.BaseParameter) => parameter.name,
    "parameter.name",
    true
  ),
  createColumn(
    "Description",
    (parameter: Api.BaseParameter) => parameter.description,
    "parameter.description",
    false
  ),
  ...parametersDateUserColumn
];

// Columns in Door Parameter submenu
export const doorParametersColumns: State.Column[] = [
  createDropdownColumn(
    "Domain",
    (parameter: Api.BaseParameter) => parameter.commonExtra?.domainName,
    "parameter.commonExtra.domainName",
    true,
    180
  ),
  createColumn(
    "Code",
    (parameter: Api.BaseParameter) => parameter.code,
    "parameter.code",
    true,
    80
  ),
  createColumn(
    "Sw Name",
    (parameter: Api.BaseParameter) => parameter.enumName,
    "parameter.enumName",
    true,
    300
  ),
  createDropdownColumn(
    "Category",
    (parameter: Api.BaseParameter) => parameter.commonExtra?.categoryName,
    "parameter.commonExtra.categoryName",
    true,
    200
  ),
  createColumn(
    "Data Format",
    (parameter: Api.DoorParameter) =>
      getKoneFormatString(decodeValueDataFormat(parameter.valueDataFormat)),
    "parameter.valueDataFormat",
    true,
    100
  ),
  createColumn(
    "Default",
    (parameter: Api.DoorParameter) => {
      const decodedValue = decodeValueDataFormat(parameter.valueDataFormat);
      const decimals =
        decodedValue.range === "Number" && decodedValue.decimals ? decodedValue.decimals : 0;
      let swDefaultValue = parameter.swDefaultValue;
      if (decimals > 0) {
        swDefaultValue *= 1 / Math.pow(10, decimals);
      }
      return swDefaultValue.toFixed(decimals);
    },
    "parameter.valueDataFormat",
    true,
    60
  ),
  createColumn(
    "Unit",
    (parameter: Api.DoorParameter) => parameter.unitName,
    "parameter.unitName",
    true,
    60
  ),
  createColumn(
    "Comment",
    (parameter: Api.DoorParameter) => parameter.comment,
    "parameter.comment",
    true
  ),
  createCheckboxColumn(
    "Requires reset",
    (parameter: Api.DoorParameter) => parameter.requiresReset,
    "parameter.requiresReset",
    true,
    100
  ),
  createCheckboxColumn(
    "Requires learning",
    (parameter: Api.DoorParameter) => parameter.requiresLearning,
    "parameter.requiresLearning",
    true,
    100
  ),
  createColumn(
    "English name",
    (parameter: Api.BaseParameter) => parameter.name,
    "parameter.name",
    true
  ),
  createColumn(
    "Description",
    (parameter: Api.BaseParameter) => parameter.description,
    "parameter.description",
    false
  ),
  ...parametersDateUserColumn
];
